// Considered: the generic SectionGroupBody property grid — rejected because the section owns hidden and
// composite fields (glyph, archetypes) that no grid row renders. See spec Reuse Audit.
import React, { useId } from 'react';
import { useTranslation } from 'localization';
import { resolveSphereGlyph } from '@center-platform/spheres/glyph';
import ArchetypesFieldMolecule from '../../../molecules/ArchetypesField/ArchetypesFieldMolecule';
import SphereDescriptionEditor from '../../../molecules/SphereProperties/SphereDescriptionEditor';
import SphereTypeAvatar from '../../../molecules/SphereTypeAvatar/SphereTypeAvatar';
import type { SphereSectionSlot, SphereSectionSlotContext } from '../sphereSectionSlots';
import './SphereIdentitySection.scss';
import { useSphereIdentityContext } from './SphereIdentityContext';

/** Stored fields the section renders as one Visual row and one Archetypes field. */
const OWNED_PROPERTIES: ReadonlySet<string> = new Set([
  'sphere_archetypes',
  'primary_archetype',
  'glyph',
  'glyph_kind',
  'glyph_color',
]);

const SphereIdentitySection: React.FC<SphereSectionSlotContext> = ({ sphere, schemaType }) => {
  const { t } = useTranslation();
  const visualLabelId = useId();
  const identity = useSphereIdentityContext();
  if (!identity) return null;
  const { archetypesState, archetypesOwnerName, isNewSphere, editable, renderVisualRow, expectedAspectsSummary } = identity;
  // The Type row shows the type's own mark, never this sphere's own glyph or colour.
  const typeMark = resolveSphereGlyph(null, { glyph: schemaType.glyph, glyphColor: schemaType.glyph_color });
  const typeSphere = { ...sphere, glyph: typeMark.glyph, glyphColor: typeMark.glyphColor };

  return (
    <div className="sphere-identity-section" data-testid="sphere-identity-section">
      {!isNewSphere && renderVisualRow && (
        <div className="sphere-identity-section__row sphere-identity-section__visual" data-testid="sphere-identity-visual">
          <span id={visualLabelId} className="sphere-identity-section__label">
            {t('sphere.visual.label')}
          </span>
          {renderVisualRow(visualLabelId)}
        </div>
      )}
      <SphereDescriptionEditor editable={editable} />
      <div className="sphere-identity-section__row sphere-identity-section__type" data-testid="sphere-identity-type">
        <span className="sphere-identity-section__label">{t('sphere.identity.type')}</span>
        <SphereTypeAvatar sphere={typeSphere} editable={false} />
        <span className="sphere-identity-section__type-name">{schemaType.name}</span>
      </div>
      {!isNewSphere && <ArchetypesFieldMolecule archetypes={archetypesState} readOnlyOwnerName={archetypesOwnerName} />}
      {expectedAspectsSummary}
    </div>
  );
};

export const identitySectionSlot: SphereSectionSlot = {
  appliesTo: () => true,
  ownsProperty: (prop) => OWNED_PROPERTIES.has(prop.name),
  Component: SphereIdentitySection,
};

export default SphereIdentitySection;
